import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LockIcon from "@mui/icons-material/LockOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../i18n";
import api from "../../api/api";
import PatientConsentGenerator from "../legal/PatientConsentGenerator";
import ComplianceDashboard from "../legal/ComplianceDashboard";
import DocumentPreviewModal, {
  type DocumentPreviewData,
} from "../legal/DocumentPreviewModal";
import { glass, glassTableSx } from "../../theme/glassTokens";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LegalLibraryDocument = {
  key: string;
  title: string;
  description: string;
  category: "patient" | "platform" | "institution" | "commercial";
  languages: string[];
  formats: string[];
  requires_signature: boolean;
  enabled: boolean;
  source_filename?: string | null;
  source_present?: boolean;
};

type LegalLibraryResponse = {
  documents: LegalLibraryDocument[];
  missing_source_docs: boolean;
};

type TierFeature = {
  id: number;
  name: string;
  description: string;
  category: string;
  category_display: string;
  order: number;
  is_active: boolean;
};

type TierSLA = {
  incident_response_hours: number;
  storage_limit_gb: number;
  max_users: number;
  uptime_sla_percent: string;
  pricing_notes: string;
};

type TierFeaturesResponse = {
  tier: {
    name: string;
    display_name: string;
    description: string;
    price_monthly_cents: number;
  };
  features: TierFeature[];
  sla: TierSLA | null;
};

type InstitutionAgreement = {
  id: string;
  document_title: string;
  status: string;
  signed_by_name: string | null;
  signed_at: string | null;
  created_at: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ContractsLegalTab() {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<LegalLibraryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Phase 2.5 state
  const [tierData, setTierData] = useState<TierFeaturesResponse | null>(null);
  const [generatingContract, setGeneratingContract] = useState(false);

  // Phase 3 state
  const [agreements, setAgreements] = useState<InstitutionAgreement[]>([]);

  // Modal state
  const [previewDoc, setPreviewDoc] = useState<DocumentPreviewData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Refresh counter to re-fetch data after signing
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const [libraryResp, tierResp, agreementsResp] = await Promise.allSettled([
          api.get<LegalLibraryResponse>("/billing/legal/library/"),
          api.get<TierFeaturesResponse>("/billing/legal/tier-features/"),
          api.get<InstitutionAgreement[]>("/billing/legal/institution/agreements/"),
        ]);

        if (!mounted) return;

        if (libraryResp.status === "fulfilled") setData(libraryResp.value.data);
        if (tierResp.status === "fulfilled") setTierData(tierResp.value.data);
        if (agreementsResp.status === "fulfilled") setAgreements(agreementsResp.value.data);
      } catch {
        if (!mounted) return;
        setError(t("billing.legal.load_error", "Failed to load legal documents."));
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [t, refreshKey]);

  const byCategory = useMemo(() => {
    const docs = data?.documents ?? [];
    return {
      templates: docs.filter((d) => ["patient", "platform", "institution"].includes(d.category)),
      commercial: docs.filter((d) => d.category === "commercial"),
    };
  }, [data?.documents]);

  const handleOpenPreview = (doc: LegalLibraryDocument) => {
    setPreviewDoc({
      key: doc.key,
      title: doc.title,
      description: doc.description,
      category: doc.category,
      languages: doc.languages,
      requires_signature: doc.requires_signature,
      enabled: doc.enabled,
    });
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDoc(null);
  };

  const handleSigned = () => {
    // Refresh agreements list after signing
    setRefreshKey((k) => k + 1);
  };

  const handleGenerateContract = async () => {
    setGeneratingContract(true);
    try {
      const resp = await api.get("/billing/legal/contract/generate/?template=unified_institutional_agreement", {
        responseType: "blob",
      });
      downloadBlob(resp.data as Blob, "unified_institutional_agreement.pdf");
    } catch {
      // Silently fail — button stops spinning
    } finally {
      setGeneratingContract(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------

  const sectionCardSx = {
    p: { xs: 1.5, sm: 2.5 },
    borderRadius: 2,
    border: `1px solid ${glass.panelBorder}`,
    background: glass.panel,
    backdropFilter: "blur(8px)",
    transition: "border-color 0.2s ease",
    "&:hover": { borderColor: glass.headerDivider },
  } as const;

  const docCardSx = (disabled: boolean) =>
    ({
      p: 2,
      borderRadius: 2,
      border: `1px solid ${disabled ? glass.rowDivider : glass.panelBorder}`,
      background: disabled ? glass.tableFrame : glass.panel,
      color: disabled ? glass.textMuted : textPrimary,
      backdropFilter: "blur(8px)",
      transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
      cursor: disabled ? "default" : "pointer",
      ...(disabled
        ? {}
        : {
            "&:hover": {
              borderColor: glass.headerDivider,
              background: glass.panel,
              boxShadow: `0 2px 12px ${glass.rowDivider}`,
            },
          }),
    }) as const;

  const statusChipColor = (status: string) => {
    switch (status) {
      case "signed":
        return { color: theme.palette.success.light, borderColor: alpha(theme.palette.success.light, 0.5) };
      case "draft":
        return { color: glass.accent, borderColor: alpha(glass.accent, 0.5) };
      case "pending_review":
        return { color: theme.palette.warning.main, borderColor: alpha(theme.palette.warning.main, 0.5) };
      default:
        return { color: glass.textSecondary, borderColor: glass.headerDivider };
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
            {t("billing.legal.title", "Contracts & Legal")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: textSecondary }}>
            {t(
              "billing.legal.subtitle",
              "Click on any document to review its contents, download it as PDF, or sign it electronically.",
            )}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: glass.headerDivider }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={sectionCardSx}>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>

            {/* =========================================================== */}
            {/* SECTION: Your Plan & Features (Phase 2.5)                    */}
            {/* =========================================================== */}
            {tierData && (
              <Box sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <DescriptionIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {t("billing.legal.your_plan", "Your Plan")}:{" "}
                    <Typography component="span" sx={{ color: glass.accent }}>{tierData.tier.display_name}</Typography>
                  </Typography>
                </Box>

                {tierData.features.length > 0 && (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small" sx={glassTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                            {t("billing.legal.feature_category", "Category")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                            {t("billing.legal.feature_name", "Feature")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                            {t("billing.legal.feature_description", "Description")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tierData.features.map((f) => (
                          <TableRow key={f.id}>
                            <TableCell>
                              <Chip
                                size="small"
                                label={f.category_display}
                                variant="outlined"
                                sx={{
                                  height: 22,
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                  borderColor: alpha(theme.palette.secondary.main, 0.3),
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{f.name}</TableCell>
                            <TableCell sx={{ fontSize: "0.78rem" }}>
                              {/* On a child: `& tbody td` outranks cell sx. */}
                              <Box component="span" sx={{ color: textSecondary }}>
                                {f.description}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tierData.sla && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: textSecondary, display: "block", mb: 0.5 }}>
                      {t("billing.legal.sla_title", "Service Level Agreement")}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip size="small" variant="outlined" label={`${t("billing.legal.uptime", "Uptime")}: ${tierData.sla.uptime_sla_percent}%`} sx={{ fontWeight: 700, fontSize: "0.72rem" }} />
                      <Chip size="small" variant="outlined" label={`${t("billing.legal.response_time", "Response")}: ${tierData.sla.incident_response_hours}h`} sx={{ fontWeight: 700, fontSize: "0.72rem" }} />
                      <Chip size="small" variant="outlined" label={`${t("billing.legal.storage", "Storage")}: ${tierData.sla.storage_limit_gb} GB`} sx={{ fontWeight: 700, fontSize: "0.72rem" }} />
                      <Chip size="small" variant="outlined" label={`${t("billing.legal.max_users", "Users")}: ${tierData.sla.max_users}`} sx={{ fontWeight: 700, fontSize: "0.72rem" }} />
                    </Stack>
                  </Box>
                )}

                <Button
                  variant="contained"
                  startIcon={
                    generatingContract ? <CircularProgress size={16} color="inherit" /> : <DescriptionIcon />
                  }
                  onClick={handleGenerateContract}
                  disabled={generatingContract}
                  sx={{ textTransform: "none", fontWeight: 800 }}
                >
                  {t("billing.legal.generate_license", "Generate My Unified Agreement")}
                </Button>
              </Box>
            )}

            {/* =========================================================== */}
            {/* SECTION: Institutional Agreements (Phase 3)                   */}
            {/* =========================================================== */}
            {agreements.length > 0 && (
              <Box sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <GavelIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {t("billing.legal.agreements_title", "Institutional Agreements")}
                  </Typography>
                </Box>

                <Stack spacing={1}>
                  {agreements.map((ag) => {
                    const chipStyle = statusChipColor(ag.status);
                    return (
                      <Box key={ag.id} sx={docCardSx(false)}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {ag.document_title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: textSecondary }}>
                              {ag.signed_by_name
                                ? `${t("billing.legal.signed_by", "Signed by")} ${ag.signed_by_name}, ${new Date(ag.signed_at!).toLocaleDateString()}`
                                : `${t("billing.legal.created", "Created")} ${new Date(ag.created_at).toLocaleDateString()}`}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            icon={
                              ag.status === "signed" ? (
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <PendingIcon sx={{ fontSize: 14 }} />
                              )
                            }
                            label={ag.status.replace("_", " ").toUpperCase()}
                            variant="outlined"
                            sx={{
                              height: 24,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              ...chipStyle,
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* =========================================================== */}
            {/* SECTION: Document Templates (clickable → modal)              */}
            {/* =========================================================== */}
            {data && !data.missing_source_docs && (
              <Box sx={sectionCardSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t("billing.legal.section_templates", "Legal Documents")}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: textSecondary }}>
                  {t(
                    "billing.legal.section_templates_desc",
                    "Click on a document to preview its contents, download it as PDF, or sign it.",
                  )}
                </Typography>
                <Stack spacing={1.25}>
                  {byCategory.templates.map((doc) => {
                    const disabled = !doc.enabled || doc.source_present === false;
                    return (
                      <ButtonBase
                        key={doc.key}
                        onClick={() => !disabled && handleOpenPreview(doc)}
                        disabled={disabled}
                        sx={{
                          display: "block",
                          textAlign: "left",
                          width: "100%",
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={docCardSx(disabled)}>
                          <Box
                            sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                  {doc.title}
                                </Typography>
                                {doc.requires_signature && (
                                  <Chip
                                    size="small"
                                    label={t("billing.legal.requires_signature", "Requires signature")}
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderColor: alpha(theme.palette.warning.main, 0.5),
                                      color: theme.palette.warning.main,
                                      fontWeight: 700,
                                    }}
                                  />
                                )}
                                {!doc.enabled && (
                                  <Chip
                                    size="small"
                                    icon={<LockIcon sx={{ fontSize: 14 }} />}
                                    label={t("billing.legal.phase2", "Phase 2")}
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderColor: glass.headerDivider,
                                      color: glass.textSecondary,
                                      fontWeight: 700,
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ mt: 0.75, color: textSecondary }}>
                                {doc.description}
                              </Typography>
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 1 }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 700, color: textSecondary }}>
                                  {t("billing.legal.languages", "Languages")}:
                                </Typography>
                                {(doc.languages || ["es"]).map((lng) => (
                                  <Chip
                                    key={`${doc.key}-${lng}`}
                                    size="small"
                                    label={lng.toUpperCase()}
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderColor: alpha(theme.palette.secondary.main, 0.25),
                                      color: glass.textSecondary,
                                      fontWeight: 700,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>

                            {/* Visual cue: eye icon or lock */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                flexShrink: 0,
                                background: disabled
                                  ? glass.panelBorder
                                  : alpha(theme.palette.secondary.main, 0.1),
                                border: `1px solid ${alpha(
                                  disabled ? glass.textAbsent : glass.accent,
                                  0.2,
                                )}`,
                              }}
                            >
                              {disabled ? (
                                <LockIcon sx={{ fontSize: 18, color: glass.textMuted }} />
                              ) : (
                                <VisibilityIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* =========================================================== */}
            {/* SECTION: Commercial Contracts (clickable → modal)             */}
            {/* =========================================================== */}
            {byCategory.commercial.length > 0 && (
              <Box sx={sectionCardSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t("billing.legal.section_commercial", "Commercial Contracts")}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mb: 1.25, color: textSecondary }}>
                  {t(
                    "billing.legal.section_commercial_desc",
                    "These templates support placeholder-filling and digital signature workflows.",
                  )}
                </Typography>
                <Stack spacing={1.25}>
                  {byCategory.commercial.map((doc) => {
                    const disabled = !doc.enabled;
                    return (
                      <ButtonBase
                        key={doc.key}
                        onClick={() => !disabled && handleOpenPreview(doc)}
                        disabled={disabled}
                        sx={{
                          display: "block",
                          textAlign: "left",
                          width: "100%",
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={docCardSx(disabled)}>
                          <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                {doc.title}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.75, color: textSecondary }}>
                                {doc.description}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                flexShrink: 0,
                                background: disabled
                                  ? glass.panelBorder
                                  : alpha(theme.palette.secondary.main, 0.1),
                                border: `1px solid ${alpha(
                                  disabled ? glass.textAbsent : glass.accent,
                                  0.2,
                                )}`,
                              }}
                            >
                              {disabled ? (
                                <LockIcon sx={{ fontSize: 18, color: glass.textMuted }} />
                              ) : (
                                <VisibilityIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* =========================================================== */}
            {/* SECTION: Patient Consent Generator (Phase 4)                  */}
            {/* =========================================================== */}
            <PatientConsentGenerator />

            {/* =========================================================== */}
            {/* SECTION: Compliance Dashboard (Phase 5d)                      */}
            {/* =========================================================== */}
            <ComplianceDashboard />

            {/* =========================================================== */}
            {/* SECTION: Missing Source Docs Warning                          */}
            {/* =========================================================== */}
            {data?.missing_source_docs && (
              <Box sx={sectionCardSx}>
                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                  {t(
                    "billing.legal.missing_source_title",
                    "Legal source documents are not available on the server.",
                  )}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: textSecondary }}>
                  {t(
                    "billing.legal.missing_source_help",
                    "Ask an administrator to configure LEGAL_DOCS_DIR or mount the legal documentation directory for the backend service.",
                  )}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Stack>

      {/* ================================================================= */}
      {/* Document Preview / Sign Modal                                     */}
      {/* ================================================================= */}
      <DocumentPreviewModal
        open={previewOpen}
        document={previewDoc}
        onClose={handleClosePreview}
        onSigned={handleSigned}
      />
    </>
  );
}
